import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { clearAuth, getUser } from '../utils/auth';
import { useTeacherPresence } from '../components/TeacherPresenceHook';
import StudentTile from '../components/StudentTile';
import ReviewInbox from '../components/ReviewInbox';
import TasksList from '../components/TasksList';
import AssignmentsList from '../components/AssignmentsList';

function TeacherDashboard() {
  const navigate = useNavigate();
  const user = getUser();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [students, setStudents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newClassName, setNewClassName] = useState('');
  const [activeTab, setActiveTab] = useState('presence'); // 'presence', 'analytics', 'assignments', 'tasks', 'review'
  const [attemptRefreshTrigger, setAttemptRefreshTrigger] = useState(0); // Incremented when new attempt comes in

  // Load teacher's classes
  useEffect(() => {
    loadClasses();
  }, []);

  const loadClasses = async () => {
    try {
      const data = await apiClient.get('/classes');
      setClasses(data.classes);
    } catch (err) {
      setError('Failed to load classes');
    }
  };

  // Load analytics when class is selected or tab changes
  const loadAnalytics = useCallback(async (classId) => {
    if (!classId) return;
    console.log('Loading analytics for class:', classId);
    try {
      const data = await apiClient.get(`/classes/${classId}/analytics`);
      console.log('Analytics loaded:', data);
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    }
  }, []);

  // Handle presence updates - memoized to prevent constant reconnects
  const handlePresenceUpdate = useCallback((data) => {
    if (data.type === 'presence_update' && data.students) {
      console.log('Updating students with presence data:', data.students);
      // Merge presence data with existing students
      setStudents(prevStudents => {
        const presenceMap = {};
        data.students.forEach(s => {
          presenceMap[s.studentId] = s;
        });

        // Update students with presence data
        if (prevStudents.length > 0) {
          return prevStudents.map(student => ({
            ...student,
            status: presenceMap[student.studentId]?.status || student.status || 'LEFT',
            lastSeenSec: presenceMap[student.studentId]?.lastSeenSec ?? student.lastSeenSec ?? 0
          }));
        }
        // If no students loaded yet, use presence data
        return data.students.map(s => ({
          studentId: s.studentId,
          displayName: s.displayName,
          status: s.status,
          lastSeenSec: s.lastSeenSec
        }));
      });
    }
  }, []);

  // Handle new attempt notifications - refresh analytics and assignments
  const handleNewAttempt = useCallback((attempt) => {
    console.log('New attempt received:', attempt);
    // Refresh analytics when a new attempt comes in
    if (selectedClass?.id) {
      loadAnalytics(selectedClass.id);
    }
    // Trigger assignment list refresh
    setAttemptRefreshTrigger(prev => prev + 1);
  }, [selectedClass?.id, loadAnalytics]);

  // WebSocket presence for selected class (teacher-specific)
  useTeacherPresence(selectedClass?.id, handlePresenceUpdate, handleNewAttempt);

  // Auto-poll analytics every 5 seconds when on analytics tab
  useEffect(() => {
    if (!selectedClass?.id || activeTab !== 'analytics') return;

    const pollInterval = setInterval(() => {
      console.log('[Analytics] Auto-polling for updates...');
      loadAnalytics(selectedClass.id);
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [selectedClass?.id, activeTab, loadAnalytics]);

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await apiClient.post('/classes', { name: newClassName });
      setNewClassName('');
      await loadClasses();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClass = async (classObj) => {
    setSelectedClass(classObj);
    setActiveTab('presence');
    setAnalytics(null); // Clear old analytics
    try {
      const data = await apiClient.get(`/classes/${classObj.id}/students`);
      console.log('Loaded students:', data.students);
      setStudents(data.students.map(s => ({
        studentId: s.id,
        displayName: s.display_name || s.username,
        status: 'LEFT',
        lastSeenSec: 0
      })));
      // Load analytics in background
      loadAnalytics(classObj.id);
    } catch (err) {
      console.error('Failed to load students:', err);
    }
  };

  const handleLogout = () => {
    clearAuth();
    navigate('/welcome');
  };

  const formatLastAttempt = (isoString) => {
    if (!isoString) return 'Pole proovinud';
    const date = new Date(isoString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just nüüd';
    if (minutes < 60) return `${minutes} min tagasi`;
    if (hours < 24) return `${hours}h tagasi`;
    return `${days}p tagasi`;
  };

  // Merge analytics data with students for display
  const getStudentWithAnalytics = (student) => {
    if (!analytics) return student;
    const analyticsData = analytics.students?.find(s => s.studentId === student.studentId);
    return {
      ...student,
      totalAttempts: analyticsData?.totalAttempts || 0,
      correctAttempts: analyticsData?.correctAttempts || 0,
      passRate: analyticsData?.passRate || 0,
      needsReviewCount: analyticsData?.needsReviewCount || 0,
      lastAttempt: analyticsData?.lastAttempt
    };
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>
          Õpetaja töölaud
        </h1>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            {user?.display_name}
          </span>
          <button
            onClick={handleLogout}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            Logi välja
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', height: 'calc(100vh - 73px)' }}>
        {/* Left Sidebar - Classes */}
        <div style={{
          width: '300px',
          background: 'white',
          borderRight: '1px solid #e5e7eb',
          padding: '24px',
          overflowY: 'auto'
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', marginBottom: '16px' }}>
            Minu klassid
          </h2>

          <form onSubmit={handleCreateClass} style={{ marginBottom: '24px' }}>
            <input
              type="text"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              placeholder="Klassi nimi"
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '14px',
                marginBottom: '8px'
              }}
            />
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                background: '#2563ff',
                color: 'white',
                border: 'none',
                fontWeight: '600',
                fontSize: '14px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              + Loo klass
            </button>
          </form>

          {error && (
            <div style={{
              padding: '12px',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#b91c1c',
              fontSize: '13px',
              marginBottom: '16px'
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {classes.map((classObj) => (
              <button
                key={classObj.id}
                onClick={() => handleSelectClass(classObj)}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: selectedClass?.id === classObj.id ? '2px solid #2563ff' : '1px solid #e5e7eb',
                  background: selectedClass?.id === classObj.id ? '#eff6ff' : 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                  {classObj.name}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Kood: {classObj.join_code}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
          {selectedClass ? (
            <>
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '8px' }}>
                  {selectedClass.name}
                </h2>
                <div style={{
                  display: 'inline-block',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: '#eff6ff',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#2563ff'
                }}>
                  Liitumise kood: {selectedClass.join_code}
                </div>
              </div>

              {/* Tab buttons */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setActiveTab('presence')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: activeTab === 'presence' ? '2px solid #2563ff' : '1px solid #e5e7eb',
                    background: activeTab === 'presence' ? '#eff6ff' : 'white',
                    color: activeTab === 'presence' ? '#2563ff' : '#6b7280',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Kohalolek
                </button>
                <button
                  onClick={() => {
                    setActiveTab('analytics');
                    loadAnalytics(selectedClass.id);
                  }}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: activeTab === 'analytics' ? '2px solid #2563ff' : '1px solid #e5e7eb',
                    background: activeTab === 'analytics' ? '#eff6ff' : 'white',
                    color: activeTab === 'analytics' ? '#2563ff' : '#6b7280',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Statistika
                </button>
                <button
                  onClick={() => setActiveTab('assignments')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: activeTab === 'assignments' ? '2px solid #8b5cf6' : '1px solid #e5e7eb',
                    background: activeTab === 'assignments' ? '#f5f3ff' : 'white',
                    color: activeTab === 'assignments' ? '#7c3aed' : '#6b7280',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Ülesanded
                </button>
                <button
                  onClick={() => setActiveTab('tasks')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: activeTab === 'tasks' ? '2px solid #6366f1' : '1px solid #e5e7eb',
                    background: activeTab === 'tasks' ? '#eef2ff' : 'white',
                    color: activeTab === 'tasks' ? '#4f46e5' : '#6b7280',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Auto-ülesanded
                </button>
                <button
                  onClick={() => setActiveTab('review')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: activeTab === 'review' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                    background: activeTab === 'review' ? '#fffbeb' : 'white',
                    color: activeTab === 'review' ? '#d97706' : '#6b7280',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  Ülevaatus
                  {analytics && analytics.needsReviewCount > 0 && (
                    <span style={{
                      background: '#f59e0b',
                      color: 'white',
                      fontSize: '11px',
                      fontWeight: '700',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      minWidth: '18px',
                      textAlign: 'center'
                    }}>
                      {analytics.needsReviewCount}
                    </span>
                  )}
                </button>
              </div>

              {/* Analytics Summary */}
              {activeTab === 'analytics' && analytics && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '16px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Õpilasi
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                      {analytics.totalStudents}
                    </div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Katseid kokku
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>
                      {analytics.totalAttempts}
                    </div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Õigeid
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e' }}>
                      {analytics.correctAttempts}
                    </div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Valesid
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>
                      {analytics.incorrectAttempts || 0}
                    </div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Ootel ülevaatus
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>
                      {analytics.needsReviewCount || 0}
                    </div>
                  </div>
                  <div style={{
                    background: 'white',
                    padding: '20px',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                      Õnnestumise %
                    </div>
                    <div style={{
                      fontSize: '28px',
                      fontWeight: '700',
                      color: analytics.passRate >= 70 ? '#22c55e' : analytics.passRate >= 40 ? '#f59e0b' : '#ef4444'
                    }}>
                      {analytics.passRate}%
                    </div>
                  </div>
                </div>
              )}

              {/* Assignments Tab */}
              {activeTab === 'assignments' && (
                <AssignmentsList classId={selectedClass.id} isTeacher={true} refreshTrigger={attemptRefreshTrigger} />
              )}

              {/* Tasks Tab (auto-detected) */}
              {activeTab === 'tasks' && (
                <TasksList classId={selectedClass.id} />
              )}

              {/* Review Tab */}
              {activeTab === 'review' && (
                <ReviewInbox classId={selectedClass.id} />
              )}

              {/* Student Grid (for presence and analytics tabs) */}
              {(activeTab === 'presence' || activeTab === 'analytics') && (
                <>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '16px'
                  }}>
                    {students.map((student) => (
                      <StudentTile
                        key={student.studentId || student.id}
                        student={getStudentWithAnalytics(student)}
                        showAnalytics={activeTab === 'analytics'}
                        formatLastAttempt={formatLastAttempt}
                      />
                    ))}
                  </div>

                  {students.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px',
                      color: '#6b7280',
                      fontSize: '16px'
                    }}>
                      Selles klassis pole veel õpilasi
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6b7280',
              fontSize: '18px'
            }}>
              Vali klass, et näha õpilasi
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TeacherDashboard;
